<template>
    <v-dialog v-model="dialogModel" fullscreen>

        <material-header
            title="Tango Card"
            info="Manage gift cards catalog"
            help="rewards"
            :loading="loading"
            icon="mdi-star-circle">

            <!-- HEADER BUTTONS -->
            <template slot="buttons">

                <v-tooltip color="primary" left v-if="hasPermission(minPermission)">
                    <template v-slot:activator="{ on }">
                        <v-btn @click="refresh()" v-on="on" icon class="mr-2">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload catalog</span>
                </v-tooltip>

                <v-tooltip color="primary" left v-if="hasPermission(minPermission)">
                    <template v-slot:activator="{ on }">
                        <v-btn @click="closeClicked()" class="elevation-1" fab small color="red" v-on="on">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Exit card management</span>
                </v-tooltip>

            </template>

        </material-header>

        <v-card v-if="!hasPermission(minPermission)">
            <v-card-text>
                <v-alert type="error" class="body-2">Your account does not have sufficient privileges for this feature</v-alert>
            </v-card-text>
        </v-card>

        <v-card flat v-else>

            <v-tabs v-model="tabModel" class="ml-5">
                <v-tab key="rewards">
                    Rewards
                </v-tab>
                <v-tab key="catalog">
                    Catalog
                </v-tab>

                <v-tab-item key="rewards">
                    <v-card flat>
                        <v-card-text v-if="enabledRewardsCount === 0">

                            <v-alert type="info" prominent class="body-2">
                                No Tango Card rewards found! Visit the Tango Card catalog and select some cards.
                            </v-alert>

                            <v-btn color="primary" @click="tabModel=1">Show catalog</v-btn>
                        </v-card-text>

                        <v-card-text>

                            <sub-panel-selector :sub-panels="subPanels"
                                                color="grey darken-2"
                                                :selected="selectedSubPanelIds"
                                                @change="subPanelFilterChanged"
                            ></sub-panel-selector>

                            <div class="pt-2">
                                <div
                                    class="pa-0 mt-2"
                                    v-for="reward in tangoRewards"
                                    :key="reward.id">

                                    <tango-card-reward-card :reward="reward">
                                    </tango-card-reward-card>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>

                </v-tab-item>

                <v-tab-item key="catalog">

                    <div class="d-flex flex-row justify-space-between ml-4 mr-10 mt-4">

                        <sub-panel-selector :sub-panels="subPanels"
                                            color="grey darken-2"
                                            :selected="selectedSubPanelIds"
                                            @change="subPanelFilterChanged"
                        ></sub-panel-selector>


                        <div style="width: 400px">

                            <v-text-field prepend-icon="mdi-magnify"
                                          hide-details
                                          color="white"
                                          solo
                                          dense
                                          background-color="grey darken-2"
                                          placeholder="Search"
                                          clearable
                                          v-model="search"
                            ></v-text-field>
                        </div>
                    </div>

                    <v-card flat>
                        <v-card-text class="pt-2">


                            <v-container fluid class="pa-0">
                                <v-row dense class="pt-1">

                                    <v-col cols="12" md="12" lg="6"
                                           class="pt-1"
                                           v-for="brand in filteredBrands"
                                           :key="brand.key">

                                        <v-lazy :height="200" transition="slide-y-reverse-transition">
                                            <card-container :key="brand.brandKey" :brand="brand"></card-container>
                                        </v-lazy>

                                    </v-col>

                                </v-row>
                            </v-container>
                        </v-card-text>
                    </v-card>
                </v-tab-item>
            </v-tabs>
        </v-card>
    </v-dialog>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../../store';
import {mapActions, mapMutations, mapState} from 'vuex';
import activeSessionChecker from '../../../../components/mixins/activeSessionCheckerMixin';
import {timeDisplay} from '../../../../components/mixins/timeDisplayMixin';
import CardContainer from './TangoCardContainer';
import MaterialHeader from '../../../../components/UI/MaterialHeader';
import PayPal from "../PayPal/PayPalManagementDialog";
import TangoCardRewardCard from "./TangoCardRewardCard";
import SubPanelSelector from "../../../../components/UI/SubPanelSelector";

const vuexModule = 'Rewards';

export default {

    mixins: [activeSessionChecker, timeDisplay],

    components: {
        SubPanelSelector,
        TangoCardRewardCard,
        Json,
        CardContainer,
        MaterialHeader,
        PayPal
    },

    props: {
        value: {
            required: true,
            type: Boolean,
        }
    },

    watch: {
        value(value) {
            if (value) {
                this.loadRewards();
                this.loadCatalog();
            }
        }
    },
    data() {
        return {
            tabModel: 0,
            minPermission: 'view rewards',
            enabledModel: true,
            render: false,
            search: '',
            currencyFilter: null,
            selectedSubPanelIds: null,
        }
    },

    computed: {

        dialogModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        filteredBrands() {

            if (this.currencyFilter) {
                return this.brands.filter(brand => {

                    let brands = brand.items.filter(item => this.currencyFilter.includes(item.currencyCode));

                    if (brands.length === 0) return false;
                    if (this.search.length === 0) {
                        return brand.brandName.includes(this.search);
                    }

                    return true;
                });
            }
        },

        ...mapState('SubPanels', [
            'subPanels'
        ]),

        ...mapState(vuexModule, [
            'brands',
            'loading',
            'tangoCardRewards'
        ]),

        tangoRewards() {
            if (this.currencyFilter) {
                return this.tangoCardRewards.filter(reward => this.currencyFilter.includes(reward.cost_currency));
            }
        },

        enabledRewardsCount() {
            return this.tangoCardRewards.filter(reward => reward.enabled).length;
        }
    },

    methods: {

        ...mapActions('SubPanels', [
            'loadSubPanels',
        ]),

        ...mapActions(vuexModule, [
            'getTangoRewards',
            'getTangoCatalog',
        ]),

        ...mapMutations(vuexModule, []),

        closeClicked() {
            this.$emit('input', false);
        },

        refresh() {
            if (this.tabModel === 0) {
                this.loadRewards();
            } else {
                this.loadCatalog(true);
            }
        },

        loadRewards(reload = false) {

            this.getTangoRewards()

                .then(() => {

                    if (reload) {
                        Bus.$emit('flash-message', {
                            message: 'Tango Card rewards reloaded!'
                        })
                    }
                })
                .catch(() => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! Tango Card rewards failed to load!'
                    })
                });
        },

        loadCatalog(reload = false) {

            this.getTangoCatalog(reload)

                .then(() => {

                    if (reload) {
                        Bus.$emit('flash-message', {
                            message: 'Catalog reloaded! Have fun!'
                        })
                    }
                })
                .catch(() => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! Tango Card catalog failed to load!'
                    })
                });
        },

        subPanelFilterChanged(subPanelIds) {

            const subPanels = this.subPanels.filter(subPanel => subPanelIds.includes(subPanel.id));

            const locales = new Set();

            subPanels.forEach(subPanel => {
                subPanel.locales.forEach(locale => {
                    locales.add(locale.currency)
                });
            });

            this.currencyFilter = Array.from(locales);
            this.selectedSubPanelIds = subPanels.map(subPanel => subPanel.id);
        }
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }

        this.loadSubPanels();
    },
}

</script>
